import graphql__388ffca368bf80ae2ded from "./__generated__/CreateSubscriptionDialogCustomerAccountsQuery.graphql.ts";
import graphql__1272337e18cae54a29a8 from "./__generated__/CreateSubscriptionDialogMutation.graphql.ts";import * as React from "react";
import {useIntl} from "react-intl";
import {commitMutation, fetchQuery, graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import Section from "../form/Section";
import Field from "../form/Field";
import Row from "../form/Row";
import Label from "../form/Label";
import * as yup from "yup";
import FormDialog from "../dialog/FormDialog";
import type {CreateSubscriptionDialogMutation} from "./__generated__/CreateSubscriptionDialogMutation.graphql";
import {useNavigate} from "react-router-dom";
import asFormikField from "../abstract/asFormikField";
import AbstractCombobox from "../abstract/AbstractCombobox";
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../../contexts/ErrorContext";

const customerAccountsDataProvider = (relayEnvironment) => {
    return AwesomeDebouncePromise((searchTerm: String = '') => {
        return fetchQuery(relayEnvironment, graphql__388ffca368bf80ae2ded, {searchTerm}).toPromise().then((data) => {
            if (!data) {
                return [];
            }
            //@ts-ignore
            return data.customerAccounts?.edges?.map((edge) => edge.node) || [];
        });
    }, 500);
}

type Props = {
    handleCloseDialog: () => {}
}

const CreateSubscriptionDialog: React.FC<Props> = ({handleCloseDialog}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const navigate = useNavigate();
    const {addError} = useErrorContext();

    const handleSubmission = (input) => {
        commitMutationWithErrorHandling<CreateSubscriptionDialogMutation>(
            relayEnvironment,
            {
                mutation: graphql__1272337e18cae54a29a8,
                variables: {
                    input: {
                        ...input,
                        customerAccountIdentifier: input.customerAccountIdentifier?.id || null
                    }
                },
                onCompleted: (response, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    handleCloseDialog();
                    navigate('/subscriptions')
                },
                onError: (error) => {
                    console.error(error);
                    handleCloseDialog();
                }
            },
            addError,
            {
                customErrorMessage: intl.formatMessage({id: 'errors.failedToCreateSubscription'})
            }
        )
    }
    const validationSchema = yup.object().shape({
        customerAccountIdentifier: yup.object()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        subscriptionServiceIdentifier: yup.string()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        subscriptionServiceReference: yup.string()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        subscriptionServiceReferenceLabel: yup.string()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        orderedAt: yup.string()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        activateAt: yup.string()
            .required(intl.formatMessage({id: 'validation.field.required'})),
        settlementType: yup.string()
            .required(intl.formatMessage({id: 'validation.field.required'})),
    });

    const FormikCombobox = asFormikField(AbstractCombobox, 'customerAccountIdentifier');

    const getEntrySecondaryLabel = (customerAccount) => {
        return customerAccount?.customerNumber
    }

    return (
        <FormDialog handleCloseDialog={handleCloseDialog} handleSubmission={handleSubmission} initialValues={{}} validationSchema={validationSchema} headline={intl.formatMessage({id: 'dialog.CreateSubscriptionDialog.headline'})} submitLabel={intl.formatMessage({id: 'button.create'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.customerAccount'})}</Label>
                    <FormikCombobox getEntryId={(customerAccount) => customerAccount.id} getEntryLabel={(customerAccount) => customerAccount && customerAccount.customerName} getEntrySecondaryLabel={getEntrySecondaryLabel} dataProvider={customerAccountsDataProvider(relayEnvironment)}/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.subscriptionService'})}</Label>
                    <Field name="subscriptionServiceIdentifier" type="text" placeholder="sbs-…"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.subscriptionServiceReference'})}</Label>
                    <Field name="subscriptionServiceReference" type="text" placeholder={intl.formatMessage({id: 'subscription.subscriptionServiceReference.placeholder'})}/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.subscriptionServiceReferenceLabel'})}</Label>
                    <Field name="subscriptionServiceReferenceLabel" type="text" placeholder={intl.formatMessage({id: 'subscription.subscriptionServiceReferenceLabel.placeholder'})}/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.subscriptionServiceReferenceUri'})}</Label>
                    <Field name="subscriptionServiceReferenceUri" type="text" placeholder="https://example.com/go/organization-xy"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.orderedAt'})}</Label>
                    <Field name="orderedAt" type="text" placeholder="yyyy-mm-dd hh:mm:ss"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.activateAt'})}</Label>
                    <Field name="activateAt" type="text" placeholder="yyyy-mm-dd hh:mm:ss"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.settlementType'})}</Label>
                    <Field name="settlementType" type="text" placeholder="Manual"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.customerOrderNotes'})}</Label>
                    <Field name="customerOrderNotes" type="text" placeholder={intl.formatMessage({id: 'subscription.customerOrderNotes.placeholder'})}/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'subscription.purchaseOrderReference'})}</Label>
                    <Field name="purchaseOrderReference" type="text" placeholder="PO-1234567"/>
                </Row>
            </Section>
        </FormDialog>
    )
}

export default CreateSubscriptionDialog;
